import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TelegramWebAppProvider } from './BenchmarkWebApp/components/TelegramWebAppProvider';
import { UserVerification, AccessDeniedCard } from './CoordinatorWebApp/components/UserVerification';
import { CoordinatorForm } from './CoordinatorWebApp/components/CoordinatorForm';
import { SubmissionAlert } from './CoordinatorWebApp/components/SubmissionAlert';
import { useCoordinatorFormSubmission } from './CoordinatorWebApp/hooks/useCoordinatorFormSubmission';
import { FarmProduct } from './CoordinatorWebApp/components/ProductSelectionField';
import { HarvestSeasonProduct } from './CoordinatorWebApp/components/HarvestDateFields';

// Telegram WebApp interface
declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          enable: () => void;
          disable: () => void;
          onClick: (callback: () => void) => void;
          offClick: (callback: () => void) => void;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          button_color?: string;
          button_text_color?: string;
          secondary_bg_color?: string;
        };
        showAlert: (message: string, callback?: () => void) => void;
        enableClosingConfirmation: () => void;
        disableClosingConfirmation: () => void;
        HapticFeedback: {
          impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
        };
      };
    };
  }
}

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface CoordinatorFormData {
  action: 'farmer_registration' | 'farm_price';
  farmerName?: string;
  farmerPhone?: string;
  farmerRegion?: string;
  farmerKebelle?: string;
  farmerCity?: string;
  farmerArea?: string;
  farmerLandSize?: string;
  harvestStartDate?: string;
  harvestEndDate?: string;
  harvestSeasonProducts?: HarvestSeasonProduct[];
  products?: FarmProduct[];
  productName?: string;
  origin?: string;
  price?: string;
  region?: string;
  notes?: string;
}

const CoordinatorWebApp: React.FC = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [date, setDate] = useState<Date>(new Date());
  const [formData, setFormData] = useState<CoordinatorFormData>({
    action: 'farmer_registration',
    harvestSeasonProducts: [],
    products: []
  });
  const [alertMessage, setAlertMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductsChange = (products: FarmProduct[]) => {
    setFormData(prev => ({ ...prev, products }));
  };

  const handleHarvestSeasonProductsChange = (harvestSeasonProducts: HarvestSeasonProduct[]) => {
    setFormData(prev => ({ ...prev, harvestSeasonProducts }));
  };

  const handleClearForm = () => {
    setFormData({
      action: formData.action, // Keep the current tab
      farmerName: '',
      farmerPhone: '',
      farmerRegion: '',
      farmerKebelle: '',
      farmerCity: '',
      farmerArea: '',
      farmerLandSize: '',
      harvestStartDate: '',
      harvestEndDate: '',
      harvestSeasonProducts: [],
      products: [],
      productName: '',
      origin: '',
      price: '',
      region: '',
      notes: '',
    });
  };

  const { isSubmitting, submitForm, submissionSteps } = useCoordinatorFormSubmission({
    userId: userId || '',
    formData,
    date,
    onSuccess: (message: string) => {
      setAlertMessage({ type: 'success', message });
      handleClearForm();
      setTimeout(() => setAlertMessage(null), 5000);
    },
    onError: (message: string) => {
      setAlertMessage({ type: 'error', message });
      setTimeout(() => setAlertMessage(null), 8000);
    }
  });

  // Show main button when form is ready
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (tg && userProfile) {
      tg.MainButton.show();
    }
  }, [userProfile]);

  // Custom verification for coordinator role
  const handleVerification = (profile: UserProfile) => {
    if (profile.role !== 'coordinator') {
      setAccessError('Access denied. Coordinator role required.');
      return;
    }
    setUserProfile(profile);
  };

  if (!userId || accessError) {
    return <AccessDeniedCard userId={userId} accessError={accessError} />;
  }

  if (!userProfile) {
    return (
      <UserVerification
        userId={userId}
        onVerified={handleVerification}
        onError={setAccessError}
      />
    );
  }

  return (
    <TelegramWebAppProvider onSubmit={submitForm} isSubmitting={isSubmitting}>
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold text-center mb-6">
          Coordinator Dashboard
        </h1>
        {alertMessage && (
          <SubmissionAlert
            type={alertMessage.type}
            message={alertMessage.message}
            onClose={() => setAlertMessage(null)}
          />
        )}
              <CoordinatorForm
                userProfile={userProfile}
                formData={formData}
                date={date}
                isSubmitting={isSubmitting}
                submissionSteps={submissionSteps}
                onInputChange={handleInputChange}
                onProductsChange={handleProductsChange}
                onHarvestSeasonProductsChange={handleHarvestSeasonProductsChange}
                onDateChange={setDate}
                onSubmit={submitForm}
              />
      </div>
    </TelegramWebAppProvider>
  );
};

export default CoordinatorWebApp;